import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const ROOM_COLORS = {
  living: "#3aa0ff", bedroom: "#a855f7", kitchen: "#f59e0b",
  bathroom: "#22c55e", balcony: "#fb7185", dining: "#22d3ee",
  study: "#fbbf24", utility: "#94a3b8", lobby: "#fca5a5",
  corridor: "#ddd6fe", stair: "#94a3b8", lift: "#f97316",
  common: "#475569",
};
const WALL_COLORS = {
  "ext-230": "#1f2937", "ext-200": "#1f2937",
  "int-115": "#475569", "int-100": "#475569",
  "partition": "#94a3b8", "shear-200": "#7c2d12",
  "shear-300": "#7c2d12", "shear-400": "#7c2d12",
  "curtain": "#3aa0ff",
};

/** Ch 25.1 — Floor-Plan Editor. */
export default function FloorPlanEditorPanel({ projectId }) {
  const [palette, setPalette] = useState(null);
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [floorIdx, setFloorIdx] = useState(0);
  const [tool, setTool] = useState("select"); // select | wall | door | window
  const [wallType, setWallType] = useState("ext-230");
  const [pendingWall, setPendingWall] = useState(null);
  const [err, setErr] = useState("");
  const svgRef = useRef(null);

  const reload = async () => {
    try {
      const [p, pl] = await Promise.all([
        api.get(`/projects/${projectId}/architecture/palette`),
        api.get(`/projects/${projectId}/architecture/floor-plans`),
      ]);
      setPalette(p.data); setPlans(pl.data.floor_plans || []);
      if (!active && pl.data.floor_plans?.[0]) setActive(pl.data.floor_plans[0]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    const name = window.prompt("Building / tower name:", `Tower ${plans.length + 1}`);
    if (!name) return;
    const plot = parseFloat(window.prompt("Plot area (m²):", "2000")) || 2000;
    try {
      const { data } = await api.post(
        `/projects/${projectId}/architecture/floor-plans`,
        { name, plot_area_m2: plot, fsi_max: 2.5, gc_max_pct: 50,
          floors: [{ id: "GF", name: "Ground Floor",
                       floor_index: 0, floor_height_m: 3,
                       walls: [
                         { type: "ext-230", thickness_mm: 230,
                           start: [0, 0], end: [12, 0] },
                         { type: "ext-230", thickness_mm: 230,
                           start: [12, 0], end: [12, 10] },
                         { type: "ext-230", thickness_mm: 230,
                           start: [12, 10], end: [0, 10] },
                         { type: "ext-230", thickness_mm: 230,
                           start: [0, 10], end: [0, 0] },
                       ], openings: [], rooms: [] }] });
      setActive(data.floor_plan); reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const persist = async (mutator) => {
    if (!active) return;
    const next = mutator({ ...active, floors: active.floors.map((f) => ({ ...f })) });
    try {
      const { data } = await api.put(
        `/projects/${projectId}/architecture/floor-plans/${active.id}`,
        next);
      setActive(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const onCanvasClick = (e) => {
    if (!active) return;
    const floor = active.floors[floorIdx];
    if (!floor) return;
    if (tool === "wall") {
      const pt = svgPoint(e, svgRef.current, viewBox);
      const snapped = [Math.round(pt.x * 4) / 4, Math.round(pt.y * 4) / 4];
      if (!pendingWall) {
        setPendingWall({ start: snapped });
      } else {
        const wt = palette.walls.find((w) => w.id === wallType);
        const newWall = { type: wallType, thickness_mm: wt?.thickness_mm,
                            start: pendingWall.start, end: snapped };
        persist((p) => {
          p.floors[floorIdx].walls = [...(p.floors[floorIdx].walls || []), newWall];
          return p;
        });
        setPendingWall(null); setTool("select");
      }
    }
  };

  const addOpening = (type) => {
    if (!active) return;
    const floor = active.floors[floorIdx];
    const wallId = floor?.walls?.[0]?.id;
    if (!wallId) return alert("Place a wall first.");
    persist((p) => {
      p.floors[floorIdx].openings = [...(p.floors[floorIdx].openings || []),
        { type, wall_id: wallId, position: 0.5, width_m: null }];
      return p;
    });
  };

  const addFloor = async () => {
    if (!active) return;
    const src = active.floors[floorIdx];
    try {
      const { data } = await api.post(
        `/projects/${projectId}/architecture/floor-plans/${active.id}/copy-floor/${src.id}`);
      const r = await api.get(
        `/projects/${projectId}/architecture/floor-plans/${active.id}`);
      setActive(r.data.floor_plan);
      setFloorIdx(active.floors.length);
      alert(`Created ${data.new_floor.name}`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const delWall = (i) => {
    persist((p) => {
      p.floors[floorIdx].walls.splice(i, 1);
      return p;
    });
  };
  const tagRoom = async (roomIdx) => {
    const name = window.prompt("Room name:", "Master Bedroom");
    if (!name) return;
    const type = window.prompt("Type (bedroom / living / kitchen / bathroom / balcony / dining / study / utility):", "bedroom");
    try {
      const floor = active.floors[floorIdx];
      await api.post(
        `/projects/${projectId}/architecture/floor-plans/${active.id}/tag-room`,
        { floor_id: floor.id, room_index: roomIdx, name, type: type || "bedroom" });
      const r = await api.get(
        `/projects/${projectId}/architecture/floor-plans/${active.id}`);
      setActive(r.data.floor_plan);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const viewBox = useMemo(() => {
    const f = active?.floors?.[floorIdx];
    if (!f?.walls?.length) return "-2 -2 20 14";
    const xs = f.walls.flatMap((w) => [w.start[0], w.end[0]]);
    const ys = f.walls.flatMap((w) => [w.start[1], w.end[1]]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w = maxX - minX || 12, h = maxY - minY || 10;
    const pad = Math.max(w, h) * 0.15 + 1;
    return `${minX - pad} ${-(maxY + pad)} ${w + 2 * pad} ${h + 2 * pad}`;
  }, [active, floorIdx]);

  if (!active) {
    return (
      <div data-testid="floor-plan-editor" style={{ padding: 24 }}>
        <div style={label}>Wave I · Ch 25.1 — Architectural Floor-Plan Editor</div>
        <p style={{ fontSize: 12, color: "var(--pf-muted)", marginBottom: 16 }}>
          No floor plans yet.
        </p>
        <button data-testid="floor-plan-new" style={btn} onClick={create}>+ New floor plan</button>
      </div>
    );
  }

  const floor = active.floors[floorIdx];

  return (
    <div data-testid="floor-plan-editor" style={{ display: "grid",
            gridTemplateColumns: "300px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 4 }}>
          <select data-testid="fp-select" style={inputStyle}
            value={active.id} onChange={(e) =>
              setActive(plans.find((p) => p.id === e.target.value))}>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button style={btnGhost} onClick={create}>+</button>
        </div>
        <div style={{ ...label, marginTop: 8 }}>Floor</div>
        <div style={{ display: "flex", gap: 4 }}>
          <select data-testid="fp-floor" style={inputStyle}
            value={floorIdx} onChange={(e) => setFloorIdx(parseInt(e.target.value))}>
            {active.floors.map((f, i) => <option key={f.id} value={i}>{f.name}</option>)}
          </select>
          <button data-testid="fp-add-floor" style={btnGhost} title="Copy current floor"
            onClick={addFloor}>+↑</button>
        </div>

        <div style={{ ...label, marginTop: 12 }}>Walls palette</div>
        <select data-testid="fp-wall-type" style={inputStyle}
          value={wallType} onChange={(e) => setWallType(e.target.value)}>
          {(palette?.walls || []).map((w) =>
            <option key={w.id} value={w.id}>{w.label}</option>)}
        </select>
        <button data-testid="fp-tool-wall"
          style={{ ...(tool === "wall" ? btn : btnGhost), width: "100%", marginTop: 4 }}
          onClick={() => { setTool(tool === "wall" ? "select" : "wall"); setPendingWall(null); }}>
          {tool === "wall" ? (pendingWall ? "Click end-point…" : "Click start-point…") : "✏ Draw wall"}
        </button>

        <div style={{ ...label, marginTop: 12 }}>Openings</div>
        {(palette?.openings || []).map((o) => (
          <button key={o.id} data-testid={`fp-open-${o.id}`}
            style={{ ...btnGhost, width: "100%", marginTop: 3, fontSize: 10, textAlign: "left" }}
            onClick={() => addOpening(o.id)}>{o.label}</button>
        ))}

        <div style={{ ...label, marginTop: 12 }}>Walls ({floor?.walls?.length || 0})</div>
        {(floor?.walls || []).map((w, i) => (
          <div key={i} data-testid={`fp-wall-row-${i}`}
            style={{ display: "flex", gap: 3, marginTop: 3, fontSize: 10,
                      alignItems: "center" }}>
            <span style={{ width: 12, height: 4, background: WALL_COLORS[w.type] || "#888" }} />
            <span style={{ flex: 1 }}>{w.id || `W${i+1}`} · {w.thickness_mm}mm</span>
            <button style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
              onClick={() => delWall(i)}>×</button>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 6, fontSize: 11, color: "var(--pf-muted)",
                       borderBottom: "1px solid var(--pf-border)" }}>
          {tool === "wall" ? "Drawing mode: click to set start, click again for end"
                              : `${active.name} · ${floor?.name} · ${floor?.walls?.length || 0} walls · ${floor?.rooms?.length || 0} rooms`}
        </div>
        <svg ref={svgRef} viewBox={viewBox} data-testid="fp-canvas"
              onClick={onCanvasClick}
              style={{ width: "100%", height: "calc(100% - 32px)", background: "#fefefe",
                       cursor: tool === "wall" ? "crosshair" : "default" }}>
          {/* Grid */}
          <defs>
            <pattern id="fpGrid" width="1" height="1" patternUnits="userSpaceOnUse">
              <path d="M 1 0 L 0 0 0 1" fill="none" stroke="#ddd" strokeWidth={0.02} />
            </pattern>
          </defs>
          <rect x={-1e3} y={-1e3} width={2e3} height={2e3} fill="url(#fpGrid)" />

          {/* Rooms */}
          {(floor?.rooms || []).map((r, ri) => (
            <g key={ri} data-testid={`fp-room-${ri}`} onClick={(e) => { e.stopPropagation(); tagRoom(ri); }}
                style={{ cursor: "pointer" }}>
              <polygon points={r.vertices_m.map((p) => `${p[0]},${-p[1]}`).join(" ")}
                          fill={ROOM_COLORS[r.type] || ROOM_COLORS.common}
                          opacity={0.30} stroke="#333" strokeWidth={0.04} />
              <text x={r.centroid_m[0]} y={-r.centroid_m[1]} fontSize={0.5}
                     textAnchor="middle" fill="#222" fontWeight="bold">{r.name}</text>
              <text x={r.centroid_m[0]} y={-r.centroid_m[1] + 0.55} fontSize={0.35}
                     textAnchor="middle" fill="#444">{r.area_m2} m²</text>
            </g>
          ))}

          {/* Walls */}
          {(floor?.walls || []).map((w, i) => {
            const t = (w.thickness_mm || 200) / 1000;
            return (
              <line key={i} x1={w.start[0]} y1={-w.start[1]}
                       x2={w.end[0]} y2={-w.end[1]}
                       stroke={WALL_COLORS[w.type] || "#333"} strokeWidth={t}
                       strokeLinecap="square" />
            );
          })}

          {/* Pending wall preview */}
          {pendingWall?.start && (
            <circle cx={pendingWall.start[0]} cy={-pendingWall.start[1]} r={0.15}
                       fill="#F26B1F" />
          )}
        </svg>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Rooms ({floor?.rooms?.length || 0})</div>
        {(floor?.rooms || []).map((r, i) => (
          <div key={i} data-testid={`fp-room-row-${i}`}
            onClick={() => tagRoom(i)}
            style={{ padding: 5, marginTop: 4, fontSize: 11, borderRadius: 3,
                      cursor: "pointer", background: "rgba(255,255,255,0.04)",
                      borderLeft: `3px solid ${ROOM_COLORS[r.type] || "#666"}` }}>
            <b>{r.name}</b> ({r.type}) · {r.area_m2} m²
          </div>
        ))}
        <p style={{ fontSize: 9, color: "#888", marginTop: 12 }}>
          Click a room on the plan or in this list to tag/rename. Rooms are auto-detected
          from enclosed wall polygons.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function svgPoint(evt, svg, viewBox) {
  const rect = svg.getBoundingClientRect();
  const [vx, vy, vw, vh] = (viewBox || "0 0 1000 1000").split(" ").map(Number);
  const xRatio = (evt.clientX - rect.left) / rect.width;
  const yRatio = (evt.clientY - rect.top) / rect.height;
  return { x: vx + xRatio * vw, y: -(vy + yRatio * vh) };
}
